/**
 * Google Analytics 4 (GA4) Integration
 * 
 * Features:
 * - Page view tracking for SPA
 * - E-commerce event tracking
 * - Conversion tracking
 * - User identification
 * - Environment-based toggling
 * - Non-blocking, fail-safe implementation
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// Configuration from environment variables (same pattern as Firebase config)
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

// Check if running on localhost (development)
const isLocalhost = 
  window.location.hostname === 'localhost' || 
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname === '[::1]';

// Toggle analytics: disabled on localhost, enabled on production domain if measurement ID is configured
const isAnalyticsEnabled = 
  !isLocalhost && 
  GA_MEASUREMENT_ID && 
  GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX' &&
  GA_MEASUREMENT_ID.startsWith('G-');

/**
 * Initialize Google Analytics
 * Called once at app bootstrap
 */
export const initGA = (): void => {
  if (!isAnalyticsEnabled) {
    if (isLocalhost) {
      console.log('[Analytics] Disabled on localhost');
    }
    return;
  }

  try {
    // Load gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer!.push(arguments);
    };

    // Configure GA4
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false, // We'll handle page views manually for SPA
      cookie_flags: 'SameSite=None;Secure'
    });

    if (isLocalhost) {
      console.log('[Analytics] GA4 initialized:', GA_MEASUREMENT_ID);
    }
  } catch (error) {
    // Silent fail in production, log in development
    if (isLocalhost) {
      console.error('[Analytics] Failed to initialize:', error);
    }
  }
};

/**
 * Track page view
 * Call this on every route change
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!isAnalyticsEnabled || !window.gtag) return;

  try {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href
    });

    if (isLocalhost) {
      console.log('[Analytics] Page view:', path);
    }
  } catch (error) {
    if (isLocalhost) {
      console.error('[Analytics] Page view tracking failed:', error);
    }
  }
};

/**
 * Set user ID after login
 * @param userId - Authenticated user ID (no PII)
 */
export const setUserId = (userId: string | null): void => {
  if (!isAnalyticsEnabled || !window.gtag) return;

  try {
    if (userId) {
      window.gtag('set', { user_id: userId });
      if (isLocalhost) {
        console.log('[Analytics] User ID set');
      }
    } else {
      window.gtag('set', { user_id: undefined });
      if (isLocalhost) {
        console.log('[Analytics] User ID cleared');
      }
    }
  } catch (error) {
    if (isLocalhost) {
      console.error('[Analytics] User ID setting failed:', error);
    }
  }
};

/**
 * Track product view
 * @param product - Product details
 */
export const trackViewItem = (product: {
  id: string;
  name: string;
  category?: string;
  price?: number;
}): void => {
  if (!isAnalyticsEnabled || !window.gtag) return;

  try {
    window.gtag('event', 'view_item', {
      currency: 'INR',
      value: product.price || 0,
      items: [{
        item_id: product.id,
        item_name: product.name,
        item_category: product.category || 'Uncategorized',
        price: product.price || 0
      }]
    });

    if (isLocalhost) {
      console.log('[Analytics] View item:', product.name);
    }
  } catch (error) {
    if (isLocalhost) {
      console.error('[Analytics] View item tracking failed:', error);
    }
  }
};

/**
 * Track add to cart (CONVERSION EVENT)
 * @param product - Product added to cart
 */
export const trackAddToCart = (product: {
  id: string;
  name: string;
  category?: string;
  price: number;
  quantity: number;
}): void => {
  if (!isAnalyticsEnabled || !window.gtag) return;

  try {
    window.gtag('event', 'add_to_cart', {
      currency: 'INR',
      value: product.price * product.quantity,
      items: [{
        item_id: product.id,
        item_name: product.name,
        item_category: product.category || 'Uncategorized',
        price: product.price,
        quantity: product.quantity
      }]
    });

    if (isLocalhost) {
      console.log('[Analytics] Add to cart:', product.name);
    }
  } catch (error) {
    if (isLocalhost) {
      console.error('[Analytics] Add to cart tracking failed:', error);
    }
  }
};

/**
 * Track begin checkout (CONVERSION EVENT)
 * @param cart - Cart items and total
 */
export const trackBeginCheckout = (cart: {
  items: Array<{
    id: string;
    name: string;
    category?: string;
    price: number;
    quantity: number;
  }>;
  value: number;
}): void => {
  if (!isAnalyticsEnabled || !window.gtag) return;

  try {
    window.gtag('event', 'begin_checkout', {
      currency: 'INR',
      value: cart.value,
      items: cart.items.map(item => ({
        item_id: item.id,
        item_name: item.name,
        item_category: item.category || 'Uncategorized',
        price: item.price,
        quantity: item.quantity
      }))
    });

    if (isLocalhost) {
      console.log('[Analytics] Begin checkout:', cart.value);
    }
  } catch (error) {
    if (isLocalhost) {
      console.error('[Analytics] Begin checkout tracking failed:', error);
    }
  }
};

/**
 * Track purchase (CONVERSION EVENT)
 * @param order - Order details
 */
export const trackPurchase = (order: {
  transactionId: string;
  value: number;
  items: Array<{
    id: string;
    name: string;
    category?: string;
    price: number;
    quantity: number;
  }>;
}): void => {
  if (!isAnalyticsEnabled || !window.gtag) return;

  try {
    window.gtag('event', 'purchase', {
      transaction_id: order.transactionId,
      currency: 'INR',
      value: order.value,
      items: order.items.map(item => ({
        item_id: item.id,
        item_name: item.name,
        item_category: item.category || 'Uncategorized',
        price: item.price,
        quantity: item.quantity
      }))
    });

    if (isLocalhost) {
      console.log('[Analytics] Purchase:', order.transactionId, order.value);
    }
  } catch (error) {
    if (isLocalhost) {
      console.error('[Analytics] Purchase tracking failed:', error);
    }
  }
};

/**
 * Track CTA clicks
 * @param name - CTA name/identifier
 * @param location - Where the CTA appears
 */
export const trackCTAClick = (name: string, location?: string): void => {
  if (!isAnalyticsEnabled || !window.gtag) return;

  try {
    window.gtag('event', 'cta_click', {
      cta_name: name,
      cta_location: location || 'unknown'
    });

    if (isLocalhost) {
      console.log('[Analytics] CTA click:', name);
    }
  } catch (error) {
    if (isLocalhost) {
      console.error('[Analytics] CTA click tracking failed:', error);
    }
  }
};

/**
 * Track custom events
 * @param eventName - Custom event name
 * @param params - Event parameters
 */
export const trackEvent = (eventName: string, params?: Record<string, any>): void => {
  if (!isAnalyticsEnabled || !window.gtag) return;

  try {
    window.gtag('event', eventName, params);
    if (isLocalhost) {
      console.log('[Analytics] Event:', eventName);
    }
  } catch (error) {
    if (isLocalhost) {
      console.error('[Analytics] Event tracking failed:', error);
    }
  }
};

// Default export for convenience
const analytics = {
  init: initGA,
  pageView: trackPageView,
  setUserId,
  viewItem: trackViewItem,
  addToCart: trackAddToCart,
  beginCheckout: trackBeginCheckout,
  purchase: trackPurchase,
  ctaClick: trackCTAClick,
  event: trackEvent
};

export default analytics;
